import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/AuthContext";
import Layout from "@/components/layout/Layout";
import Dashboard from "@/pages/Dashboard";
import BoardPage from "@/pages/BoardPage";
import CrossProjectPage from "@/pages/CrossProjectPage";
import AdminSlackSettings from "@/pages/AdminSlackSettings";
import AdminUsersPage from "@/pages/AdminUsersPage";
import ProfilePage from "@/pages/ProfilePage";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import { fetchUsers, fetchProjects } from "@/lib/api";
import { Toaster } from "@/components/ui/sonner";

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-[#F7F5F3]">
        <div className="text-sm text-gray-500">Loading...</div>
      </div>
    );
  }
  if (user === false) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

function AppContent() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);

  const loadGlobalData = useCallback(async () => {
    if (!user || user === false) return;
    try {
      const [u, p] = await Promise.all([fetchUsers(), fetchProjects()]);
      setUsers(u);
      setProjects(p);
    } catch (e) {
      console.error("Failed to load global data", e);
    }
  }, [user]);

  useEffect(() => {
    loadGlobalData();
  }, [loadGlobalData]);

  return (
    <Routes>
      <Route path="/login" element={
        user && user !== false ? <Navigate to="/dashboard" replace /> : <LoginPage />
      } />
      <Route path="/signup" element={
        user && user !== false ? <Navigate to="/dashboard" replace /> : <SignupPage />
      } />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <Layout users={users} projects={projects} currentUser={user} onProjectCreated={loadGlobalData}>
              <Routes>
                <Route path="/dashboard" element={<Dashboard users={users} projects={projects} />} />
                <Route
                  path="/cross-project"
                  element={<CrossProjectPage users={users} projects={projects} currentUser={user} />}
                />
                <Route
                  path="/admin/users"
                  element={
                    <AdminUsersPage currentUser={user} onUsersChanged={loadGlobalData} />
                  }
                />
                <Route
                  path="/admin/slack"
                  element={<AdminSlackSettings users={users} />}
                />
                <Route
                  path="/profile"
                  element={<ProfilePage currentUser={user} users={users} />}
                />
                <Route
                  path="/project/:projectKey/:boardType"
                  element={<BoardPage users={users} projects={projects} currentUser={user} />}
                />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </Layout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter basename="/dashboard">
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="bottom-right" />
    </div>
  );
}

export default App;
